import React from 'react';
import styled from 'styled-components';
import { Table as AntTable } from 'antd';
import { windowMaxWidth } from '../Layout/breakpoints';
import IconSorterDown from '../Icons/IconSorterDown';
import IconSorterUp from '../Icons/IconSorterUp';

const SortChevron = styled.span`
  align-items: center;
  display: inline-flex;
  margin-left: 2px;

  svg path {
    stroke: var(--primary-oqio-blue);
  }
`;

const getSortIcon = ({ sortOrder }) => {
  if (!sortOrder) return null;
  return <SortChevron>{sortOrder === 'ascend' ? <IconSorterUp /> : <IconSorterDown />}</SortChevron>;
};

const injectSortIcon = (columns) => {
  if (!columns) return columns;
  return columns.map((col) => {
    const result = { ...col };
    if (col.sorter && !col.sortIcon) {
      result.sortIcon = getSortIcon;
    }
    if (col.children) {
      result.children = injectSortIcon(col.children);
    }
    return result;
  });
};

const StyledTable = styled(AntTable)`
  &.ant-table-wrapper {
    position: relative;
    scrollbar-color: rgb(136 136 136 / 30%) transparent !important;
    width: 100%;

    .ant-table {
      background: var(--greyscale-white);
      color: var(--greyscale-blackish);

      &.ant-table-empty {
        .ant-table-body {
          max-height: calc(100vh - 300px) !important;
        }

        .ant-table-content {
          overflow: hidden;
        }
      }
    }

    .ant-table-thead {
      th.ant-table-cell {
        background: var(--greyscale-white);
        border-bottom: 1px solid var(--greyscale-dark-white);
        color: var(--greyscale-shade);
        flex: 0 1 auto;
        font-size: var(--font-size-small);
        font-weight: var(--font-weight-regular);
        height: 40px;
        line-height: var(--font-line-height-regular);
        padding: 10px;

        &:first-child {
          padding-left: 20px;
        }

        &:last-child {
          padding-right: 20px;
        }

        .ant-table-column-sorters {
          justify-content: flex-start;

          .ant-table-column-title {
            flex: 0 1 auto;
          }
        }

        &::before {
          content: none !important;
        }

        &.ant-table-column-sort {
          font-weight: var(--font-weight-medium);
        }

        &.ant-table-column-has-sorters:hover {
          color: var(--greyscale-shade);
        }
      }

      .ant-table-column-sorter-up,
      .ant-table-column-sorter-down {
        display: none;
      }
    }

    .ant-table-tbody {
      tr.ant-table-row {
        &.row-pointer {
          cursor: pointer;

          .ant-table-cell,
          .ant-table-cell-row-hover {
            cursor: pointer;
          }
        }

        &.active {
          background-color: var(--secondary-whisper);

          td.ant-table-column-sort {
            background-color: var(--secondary-whisper);
          }
        }

        .ant-table-cell-row-hover {
          background: var(--greyscale-porcelain);
        }
      }

      td.ant-table-cell {
        vertical-align: top;
        border-color: var(--greyscale-dark-white);
        line-height: var(--font-line-height-regular) !important;
        padding: 20px 10px;

        &.cell-align-bottom {
          vertical-align: bottom;
        }

        &.cell-align-middle {
          vertical-align: middle;
        }

        &:first-child {
          padding-left: 20px;
        }

        &:last-child {
          padding-right: 20px;
        }

        span:not(.ant-tag):not(.count-pill):not(.ich-badge):not(.status-dot) {
          display: inline;
          line-height: var(--font-line-height-regular);
          max-width: 100%;
          overflow: hidden;
          padding: 0;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    &.table-within-modal {
      max-height: 300px;
      min-height: 300px;

      @media (${windowMaxWidth.mobile}) {
        min-height: 230px;
      }

      .ant-table-thead {
        th.ant-table-cell {
          &:first-child {
            padding-left: 40px;
          }

          &:last-child {
            padding-right: 40px;
          }
        }
      }

      .ant-table-tbody {
        td.ant-table-cell {
          padding: 2px 10px;

          &:first-child {
            padding-left: 40px;
          }

          &:last-child {
            padding-right: 40px;
          }
        }
      }
    }

    tr.ant-table-placeholder {
      background: var(--greyscale-white);

      .ant-table-cell {
        border-bottom: none;
      }

      &:hover {
        td {
          background: none;
        }
      }
    }

    @media (${windowMaxWidth.mobile}) {
      margin-bottom: 70px !important;
    }

    &.claim-details-settlements-table {
      margin-bottom: 0 !important;

      /* stylelint-disable-next-line no-descending-specificity */
      .ant-table-cell {
        font-weight: var(--font-weight-medium);
        vertical-align: top;
      }

      .ant-table-tbody,
      .ant-table-thead {
        .ant-table-cell {
          &:first-child {
            padding-left: 30px;
          }

          &:last-child {
            padding-right: 30px;
          }
        }
      }
    }

    &.policy-details-premiums-table {
      .ant-table-tbody {
        tr.ant-table-row {
          cursor: default;

          &:hover {
            td {
              background: initial;
            }
          }

          td.ant-table-cell {
            padding-bottom: 10px;
            padding-top: 10px;
          }

          &.highlight-row {
            td.ant-table-cell {
              font-weight: var(--font-weight-semi-bold);
              padding-bottom: 20px;
              padding-top: 20px;
            }
          }
        }
      }

      .ant-table-tbody,
      .ant-table-thead {
        .ant-table-cell {
          &:first-child {
            padding-left: 30px;
          }

          &:last-child {
            padding-right: 30px;
          }
        }
      }
    }

    &.details-tab-claims-table {
      .ant-table-tbody {
        .ant-table-cell {
          padding: 15px 10px;

          div {
            overflow: hidden;
          }
        }
      }
    }
  }
`;

const Table = ({ columns, ...props }) => <StyledTable columns={injectSortIcon(columns)} {...props} />;

export default Table;
